import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, Filter, Eye } from 'lucide-react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { GanttChart } from '../components/calendar/GanttChart';
import { getAccessibleProjects, getServiceOrders } from '../services/api';
import { ServiceOrderWithTasks } from '../types/serviceOrder';

interface Project {
  _id: string;
  name: string;
  description?: string;
  hoursTrackingEnabled?: boolean;
  jiraEnabled?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface GanttData {
  projects: {
    id: string;
    name: string;
    serviceOrders: ServiceOrderWithTasks[];
  }[];
  dateRange: { start: Date; end: Date };
}

const Calendar: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ganttData, setGanttData] = useState<GanttData | null>(null);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState<'month' | 'quarter'>('quarter');

  // Calculate date range based on current date and view mode
  const getDateRange = (date: Date, mode: 'month' | 'quarter') => {
    const start = new Date(date);
    const end = new Date(date);

    if (mode === 'month') {
      // Show current month + 2 months ahead
      start.setDate(1);
      end.setMonth(end.getMonth() + 3);
      end.setDate(0); // Last day of the month
    } else {
      // Show current quarter + next quarter
      const quarter = Math.floor(date.getMonth() / 3);
      start.setMonth(quarter * 3, 1);
      end.setMonth(quarter * 3 + 6, 0);
    }

    return { start, end };
  };

  const loadGanttData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get all accessible projects (owned + team member)
      const projectsResponse = await getAccessibleProjects();
      const projects: Project[] = projectsResponse.data.data.projects;

      // Get service orders for each project
      const ganttProjects = await Promise.all(
        projects.map(async (project) => {
          try {
            const serviceOrdersResponse = await getServiceOrders(project._id);
            return {
              id: project._id,
              name: project.name,
              serviceOrders: serviceOrdersResponse.data.data.serviceOrders || []
            };
          } catch (err) {
            console.error(`Error loading service orders for project ${project.name}:`, err);
            return {
              id: project._id,
              name: project.name,
              serviceOrders: []
            };
          }
        })
      );

      // Filter out projects with no service orders
      const projectsWithServiceOrders = ganttProjects.filter(
        project => project.serviceOrders.length > 0
      );

      const dateRange = getDateRange(currentDate, viewMode);

      setGanttData({
        projects: projectsWithServiceOrders,
        dateRange
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar dados do calendário');
      console.error('Error loading gantt data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGanttData();
  }, [currentDate, viewMode]);

  const handlePreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const formatPeriod = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('pt-BR', { 
        month: 'long', 
        year: 'numeric' 
      });
    } else {
      const quarter = Math.floor(currentDate.getMonth() / 3) + 1;
      return `Q${quarter} ${currentDate.getFullYear()}`;
    }
  };

  if (loading) {
    return (
      <Layout currentPage="calendar">
        <div className="h-full flex items-center justify-center">
          <LoadingSpinner message="Carregando calendário" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout currentPage="calendar">
      <div className="h-full overflow-y-auto custom-scrollbar">
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                
                <h1 className="text-3xl font-bold text-foreground-primary">
                  Calendário de Projetos
                </h1>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'month' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('month')}
                >
                  Mensal
                </Button>
                <Button
                  variant={viewMode === 'quarter' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('quarter')}
                >
                  Trimestral
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          <Card variant="glass" className="mb-6">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handlePreviousMonth}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <h2 className="text-xl font-heading text-foreground-primary">
                    {formatPeriod()}
                  </h2>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleNextMonth}
                    className="h-8 w-8 p-0"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToday}
                  >
                    Hoje
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Error Message */}
          {error && (
            <Card variant="glass" className="border-red-500/30 bg-red-500/10 mb-6">
              <CardContent className="p-4">
                <div className="text-red-400">{error}</div>
              </CardContent>
            </Card>
          )}

          {/* GANTT Chart */}
          {ganttData && (
            <Card variant="glass">
              <CardHeader>
                <CardTitle className="text-lg text-foreground-primary">
                  Timeline das Ordens de Serviço
                </CardTitle>
              </CardHeader>
              <CardContent>
                <GanttChart data={ganttData} />
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!loading && ganttData && ganttData.projects.length === 0 && (
            <Card variant="glass">
              <CardContent className="p-8 text-center">
                <CalendarIcon className="w-12 h-12 text-foreground-tertiary mx-auto mb-4" />
                <h3 className="text-lg font-heading text-foreground-primary mb-2">
                  Nenhuma ordem de serviço encontrada
                </h3>
                <p className="text-foreground-tertiary">
                  Crie ordens de serviço nos seus projetos para visualizar o timeline
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Calendar;